import axios from 'axios'
import apiClient, { clearFingerprint } from '@/api'
import {
  setFingerprint,
  setIamAccessToken,
  setSessionId,
  clearPendingIamMfaToken,
  setPendingIamMfaToken,
  getPendingIamMfaToken,
} from '@/api/authSession'
import type { ApiResponse, User } from '@/types'

function isLikelyOtpFieldName(key: string): boolean {
  const n = key.replace(/_/g, '').toLowerCase()
  if (n === 'otp' || n === 'mfacode' || n === 'devotp' || n === 'developmentotp') return true
  if (n === 'otpbypass') return true
  if (n === 'verificationcode' || n === 'onetimepassword') return true
  if (n === 'code') return true
  if (n.endsWith('otp') || n.includes('mfacode')) return true
  return false
}

function readSixDigitOtpValue(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const t = v.trim()
    if (/^\d{6}$/.test(t)) return t
  }
  if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 999999) {
    return String(v).padStart(6, '0')
  }
  return undefined
}

export function extractOtpBypassFromLoginPayload(payload: unknown): string | undefined {
  if (payload == null || typeof payload !== 'object') return undefined
  const visited = new Set<unknown>()

  function walk(node: unknown, depth: number): string | undefined {
    if (depth > 12 || node == null || typeof node !== 'object') return undefined
    if (visited.has(node)) return undefined
    visited.add(node)

    if (Array.isArray(node)) {
      for (const item of node) {
        const found = walk(item, depth + 1)
        if (found) return found
      }
      return undefined
    }

    const rec = node as Record<string, unknown>
    for (const [k, v] of Object.entries(rec)) {
      const nk = k.replace(/_/g, '').toLowerCase()
      if (nk === 'otpbypass') {
        const got = readSixDigitOtpValue(v)
        if (got) return got
      }
    }
    for (const v of Object.values(rec)) {
      const found = walk(v, depth + 1)
      if (found) return found
    }
    return undefined
  }

  return walk(payload, 0)
}

export function extractDevOtpFromLoginPayload(data: unknown): string | undefined {
  const visited = new Set<unknown>()

  function walk(node: unknown): string | undefined {
    if (node === null || node === undefined) return undefined
    if (typeof node !== 'object') return undefined
    if (visited.has(node)) return undefined
    visited.add(node)

    if (Array.isArray(node)) {
      for (const item of node) {
        const found = walk(item)
        if (found) return found
      }
      return undefined
    }

    const rec = node as Record<string, unknown>
    for (const [k, v] of Object.entries(rec)) {
      if (!isLikelyOtpFieldName(k)) continue
      if (typeof v === 'string' && /^\d{6}$/.test(v)) return v
      if (typeof v === 'number' && Number.isInteger(v) && v >= 100000 && v <= 999999) {
        return String(v).padStart(6, '0')
      }
    }
    for (const v of Object.values(rec)) {
      const found = walk(v)
      if (found) return found
    }
    return undefined
  }

  return walk(data)
}

function readSessionString(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const t = v.trim()
  return t.length > 0 ? t : undefined
}

function extractVerifyPayload(body: unknown): {
  fingerprint?: string
  token?: string
  sessionId?: string
} {
  if (body == null || typeof body !== 'object') return {}

  const visited = new Set<unknown>()
  let fingerprint: string | undefined
  let token: string | undefined
  let sessionId: string | undefined

  function consider(key: string, value: unknown) {
    const nk = key.replace(/_/g, '').toLowerCase()
    if (
      !fingerprint &&
      (nk === 'fingerprint' || nk === 'xsrf' || nk === 'xsrftoken' || nk === 'xsrfingerprint')
    ) {
      fingerprint = readSessionString(value)
    }
    if (
      !token &&
      (nk === 'token' ||
        nk === 'accesstoken' ||
        nk === 'jwt' ||
        nk === 'bearertoken' ||
        nk === 'sessiontoken')
    ) {
      token = readSessionString(value)
    }
    if (!sessionId && nk === 'sessionid') {
      sessionId = readSessionString(value)
    }
  }

  function walk(node: unknown, depth: number) {
    if (depth > 14 || node == null || typeof node !== 'object') return
    if (visited.has(node)) return
    visited.add(node)

    if (Array.isArray(node)) {
      for (const item of node) walk(item, depth + 1)
      return
    }

    const rec = node as Record<string, unknown>
    for (const [k, v] of Object.entries(rec)) {
      consider(k, v)
      walk(v, depth + 1)
    }
  }

  walk(body, 0)
  return { fingerprint, token, sessionId }
}

/**
 * Auth against RFID_NJ_BE proxies (which forward to IAM).
 * Same session/MFA pattern as POD_RE_FE — no direct IAM calls from the browser.
 */
export const authService = {
  async login(identifier: string, password: string) {
    clearFingerprint()
    clearPendingIamMfaToken()
    const response = await apiClient.post('/api/auth/login', { identifier, password })
    const payload = response.data
    const mfaTok = payload?.data?.mfa_token ?? payload?.mfa_token
    if (typeof mfaTok === 'string' && mfaTok.trim()) {
      setPendingIamMfaToken(mfaTok.trim())
    }
    return payload
  },

  async verifyOtp(otp: string) {
    const mfa = getPendingIamMfaToken()
    const headers: Record<string, string> = {}
    if (mfa) {
      headers['x-mfa-token'] = mfa
    }

    try {
      const response = await apiClient.post(
        '/api/auth/verify-otp',
        mfa ? { otp, mfa_token: mfa } : { otp },
        { headers }
      )

      const extracted = extractVerifyPayload(response.data)
      if (!extracted.fingerprint || !extracted.token) {
        throw new Error(
          'OTP accepted but session tokens were missing from the auth response. Try signing in again.'
        )
      }

      setFingerprint(extracted.fingerprint)
      setIamAccessToken(extracted.token)
      if (extracted.sessionId) setSessionId(extracted.sessionId)
      else setSessionId('')

      clearPendingIamMfaToken()
      return response.data
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const body = e.response?.data as Record<string, unknown> | undefined
        const raw =
          (typeof body?.message === 'string' && body.message) ||
          (typeof body?.error === 'string' && body.error) ||
          ''
        const msg =
          typeof raw === 'string' && raw.trim()
            ? raw.trim()
            : e.response?.status === 400 && !mfa
              ? 'MFA session missing; try signing in again.'
              : 'OTP verification failed.'
        throw new Error(msg)
      }
      if (e instanceof Error) throw e
      throw new Error('OTP verification failed.')
    }
  },

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await apiClient.get('/api/auth/me')
    return response.data
  },

  async logout(): Promise<ApiResponse<unknown>> {
    try {
      const res = await apiClient.post('/api/auth/logout', {})
      return res.data
    } catch {
      return { success: true, data: null as unknown }
    } finally {
      clearPendingIamMfaToken()
      clearFingerprint()
    }
  },
}
